/**
 * ReviewQueue — pending-review section at the top of GardenStudio.
 *
 * Phase 4 emits acceptance failures as a flat `string[]` (no per-criterion
 * observed/threshold object), so we render that list directly.
 */
import { useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { gardenApi, type GardenRun } from '@/services/api';
import { useAppStore } from '@/stores/app';
import { ConfirmDialog } from '@/components/pdo/ConfirmDialog';

interface Props {
  clientUid: string;
  runs: GardenRun[];
  onAction: () => void;
}

export function ReviewQueue({ clientUid, runs, onAction }: Props) {
  return (
    <section className="mt-3 bg-purple-50/40 border border-purple-200 rounded-xl p-4">
      <header className="flex items-center gap-2 mb-3">
        <AlertTriangle size={15} className="text-purple-600" />
        <h2 className="text-sm font-semibold text-purple-900">Pending review</h2>
        <span className="text-xs font-mono text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
          {runs.length}
        </span>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {runs.map((r) => (
          <ReviewCard
            key={r.run_id}
            clientUid={clientUid}
            run={r}
            onAction={onAction}
          />
        ))}
      </div>
    </section>
  );
}

function ReviewCard({
  clientUid,
  run,
  onAction,
}: {
  clientUid: string;
  run: GardenRun;
  onAction: () => void;
}) {
  const addNotification = useAppStore((s) => s.addNotification);
  const [approve, setApprove] = useState(false);
  const [reject, setReject] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const failures = run.acceptance_result.failures ?? [];

  const doApprove = async () => {
    setBusy(true);
    try {
      await gardenApi.approveRun(clientUid, run.run_id);
      addNotification('success', `Approved run ${run.run_id}`);
      setApprove(false);
      onAction();
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await gardenApi.rejectRun(clientUid, run.run_id, comment.trim());
      addNotification('success', `Rejected run ${run.run_id}`);
      setReject(false);
      setComment('');
      onAction();
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-purple-200 rounded-lg p-3 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-mono text-xs text-navy-900 truncate">
          {run.run_id}
        </span>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
          {run.run_type}
        </span>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <span className="text-slate-500">
            +{' '}
            <span className="font-mono text-emerald-700">
              {run.diff_summary.concepts_added}
            </span>{' '}
            concepts
          </span>
          <span className="text-slate-500">
            −{' '}
            <span className="font-mono text-red-700">
              {run.diff_summary.concepts_removed}
            </span>{' '}
            concepts
          </span>
          <span className="text-slate-500">
            Δ{' '}
            <span className="font-mono text-amber-700">
              {run.diff_summary.concepts_modified}
            </span>{' '}
            modified
          </span>
        </div>

        {failures.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-red-600 mb-1">
              Acceptance failures
            </p>
            <ul className="space-y-0.5 text-xs text-red-700">
              {failures.slice(0, 4).map((f, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <XCircle size={10} className="flex-shrink-0 mt-0.5" />
                  <span className="font-mono break-words">{f}</span>
                </li>
              ))}
              {failures.length > 4 && (
                <li className="text-red-500/70 italic">
                  +{failures.length - 4} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => setReject(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded border border-red-200 text-red-700 hover:bg-red-50"
        >
          <XCircle size={11} />
          Reject
        </button>
        <button
          type="button"
          onClick={() => setApprove(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700"
        >
          <CheckCircle2 size={11} />
          Approve
        </button>
      </div>

      <ConfirmDialog
        open={approve}
        title="Approve and publish"
        message={
          <p>
            Approve <span className="font-mono font-semibold">{run.run_id}</span> and
            publish the resulting PDO version? Downstream agents will switch to it.
          </p>
        }
        confirmText="approve"
        confirmLabel="Approve & Publish"
        busy={busy}
        onCancel={() => !busy && setApprove(false)}
        onConfirm={doApprove}
      />

      {reject && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-end"
          onClick={() => !busy && setReject(false)}
        >
          <div className="flex-1 bg-black/30 backdrop-blur-sm" />
          <div
            className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-navy-900">Reject run</h3>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{run.run_id}</p>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Rejection reason (required)
              </label>
              <textarea
                rows={6}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Why is this run being rejected? This is recorded with the run for audit."
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
              {failures.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                    {failures.length} acceptance failure(s)
                  </summary>
                  <ul className="mt-1 space-y-0.5 text-red-700 font-mono">
                    {failures.map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => !busy && setReject(false)}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doReject}
                disabled={busy || !comment.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy && <Loader2 size={13} className="animate-spin" />}
                Confirm Rejection
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
